/**
 * Suspicious v2 §3.1 + founder 2026-10-18 — one card per sender IP in
 * the Suspicious list.
 *
 * New lead copy: a plain-language sentence that puts the location
 * first and the attack verb second — "Someone from Frankfurt,
 * Germany tried to run a script in uploads." Falls back to
 * region/country and finally "an unknown location". Datacenters:
 * no extra "hosting network" tag in the sentence (the merchant can
 * see the network on line 2 and in the detail).
 *
 * IP is small/muted on line 2 next to the network label.
 *
 * Severity is one icon (ShieldAlert) colour-coded by severity + a
 * Tooltip that explains what the severity word means.
 */

import type { CSSProperties, ReactNode } from 'react';

import { RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { CountryFlag, countryName } from '../../components/atoms/CountryFlag.js';
import { useC } from '../../lib/theme.js';
import type { ThreatSenderSummary } from '@sill/types/api';
import { severityIcon } from './severity-icon.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface SenderCardProps {
  readonly sender: ThreatSenderSummary;
  readonly isSelected: boolean;
  readonly isLast: boolean;
  readonly onSelect: () => void;
  /**
   * ADR-0248 S2 — the campaign this sender participates in, or null.
   * When set, the card renders a small "in campaign" chip under the
   * state line.
   */
  readonly campaignId?: string | null;
  readonly onSelectCampaign?: (campaign_id: string) => void;
  /** Show a NEW tag (first seen on this site in the last 24 hours). */
  readonly isNew?: boolean;
}

function networkLine(nt: ThreatSenderSummary['network_type'], as_org: string | null): string {
  if (nt === 'hosting') return as_org ?? 'Hosting network';
  if (nt === 'residential') return 'Home or mobile network';
  return as_org ?? '';
}

function stateLine(sender: ThreatSenderSummary): string | null {
  if (sender.state === 'blocked' && sender.block_expires_at !== null) {
    const d = new Date(sender.block_expires_at);
    const label = Number.isNaN(d.getTime())
      ? 'until expiry'
      : `until ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d)}`;
    return `Blocked · ${label}`;
  }
  if (sender.state === 'blocked') return 'Blocked';
  if (sender.state === 'block_saved') return 'Block saved · not active';
  if (sender.state === 'trusted') return 'Trusted';
  if (sender.state === 'ignored') return 'Ignored';
  return null;
}

/** "Someone from Frankfurt, Germany {verb}." */
export function senderLeadSentence(sender: ThreatSenderSummary): string {
  const label = sender.class_labels[0];
  // Lower-case the first word so it reads mid-sentence after "Someone from X".
  const verb = label === undefined || label.length === 0
    ? 'did something suspicious'
    : label.charAt(0).toLowerCase() + label.slice(1);
  const country = countryName(sender.country) ?? sender.country ?? null;
  const region = typeof sender.region === 'string' && sender.region.length > 0 ? sender.region : null;
  const location = country === null && region === null
    ? 'an unknown location'
    : region === null ? country!
      : country === null ? region
        : `${region}, ${country}`;
  return `Someone from ${location} ${verb}.`;
}

export function SenderCard({
  sender,
  isSelected,
  isLast,
  onSelect,
  campaignId,
  onSelectCampaign,
  isNew = false,
}: SenderCardProps): JSX.Element {
  const C = useC();
  const sev = severityIcon(sender.max_severity, C);
  const state_line = stateLine(sender);
  const lead = senderLeadSentence(sender);
  const network = networkLine(sender.network_type, sender.as_org);
  const succeededCount = sender.any_succeeded;
  const right_count = succeededCount ? (
    <span style={{ color: C.coralFg, fontFamily: monoFont, fontSize: 10 }}>
      {/* The list only knows that SOME request succeeded (a 2xx on an
          attack path), not how many: never pin the total on it. */}
      {'●'} A request succeeded
    </span>
  ) : (
    <span style={{ color: sev.colour, fontFamily: monoFont, fontSize: 10, letterSpacing: 0.5 }}>
      {sender.requests_display}
    </span>
  );

  const cardStyle: CSSProperties = {
    cursor: 'pointer',
    background: isSelected ? C.surface2 : 'transparent',
    borderLeft: `2px solid ${isSelected ? C.limeFg : sev.colour}`,
    borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
    transition: 'background-color 300ms ease-out',
    padding: 16,
  };
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="sender-card"
      data-sender-ref={sender.sender_ref}
      data-severity={sender.max_severity}
      data-state={sender.state}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      aria-pressed={isSelected}
      aria-label={`${sender.max_severity} suspicious sender ${sender.sender_ip_display}`}
      className="w-full text-left"
      style={cardStyle}
    >
      {/* Wraps at phone width so the right-hand status drops under the
          sentence instead of squeezing it (founder review 2026-10-20). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 8, rowGap: 4, minWidth: 0 }}>
        <Tooltip content={sev.tip}>
          <span data-testid="sender-severity" aria-label={sev.tip} style={{ display: 'inline-flex', flexShrink: 0, marginTop: 2 }}>
            {sev.node as ReactNode}
          </span>
        </Tooltip>
        {sender.country !== null ? (
          <span style={{ flexShrink: 0, marginTop: 2 }}>
            <CountryFlag code={sender.country} size={16} />
          </span>
        ) : null}
        <span
          data-testid="sender-lead"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 600,
            overflowWrap: 'anywhere',
            flex: '1 1 180px',
            minWidth: 0,
          }}
        >
          {lead}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
          {right_count}
          <RelativeTime iso={sender.last_seen} style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }} />
        </span>
      </div>
      <div
        data-testid="sender-ip-line"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          // Founder report 2026-10-20 item 3: on phone widths (390 px)
          // the NEW pill's BOTTOM border was clipped. The row previously
          // ran `overflow: hidden` as a belt-and-braces to clip long
          // IPs, but the pill is taller than the mono line's natural
          // cross-axis height and gets cut by that same `overflow`.
          // The IP span itself still clips, so dropping `overflow:
          // hidden` on the row is safe. We also lift `line-height` to
          // 1.8 so the pill (fontSize 9 + 2 px padding + 2 px border)
          // sits comfortably within the row and the border stays whole.
          marginTop: 8,
          lineHeight: 1.8,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 6,
        }}
      >
        {isNew ? (
          <span
            data-testid={`attackers-new-tag-${sender.sender_ref}`}
            style={{
              fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, lineHeight: 1.4,
              color: C.limeFg, border: `1px solid ${C.limeFg}`, borderRadius: 2,
              padding: '2px 5px',
              display: 'inline-flex', alignItems: 'center',
              flexShrink: 0,
            }}
          >
            NEW
          </span>
        ) : null}
        <span style={{ color: C.body, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', minWidth: 0 }}>{sender.sender_ip_display}</span>
        {network.length > 0 ? <span style={{ whiteSpace: 'nowrap' }}>&middot; {network}</span> : null}
      </div>
      {state_line !== null ? (
        <div
          data-testid="sender-state-line"
          data-state={sender.state}
          style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 0.3, marginTop: 6 }}
        >
          {state_line}
        </div>
      ) : null}
      {/* Muted meta chips: "seen before · N in 30 days" when the sender
          has prior alerts on this site, and "also on N other Sill sites"
          when the fleet counter observed the same address elsewhere in
          the trailing 24 h. Both render only when the server attested a
          value > 0. */}
      {renderMetaChips(sender)}
      {typeof campaignId === 'string' && campaignId.length > 0 ? (
        <button
          type="button"
          data-testid="sender-campaign-chip"
          data-campaign-id={campaignId}
          onClick={(e) => {
            e.stopPropagation();
            onSelectCampaign?.(campaignId);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          style={{
            marginTop: 6,
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '2px 6px',
            fontFamily: monoFont,
            fontSize: 9,
            letterSpacing: 1.2,
            color: C.limeFg,
            cursor: 'pointer',
          }}
        >
          IN CAMPAIGN &rarr;
        </button>
      ) : null}
    </div>
  );
}

function MetaChip({
  testid,
  children,
}: {
  readonly testid: string;
  readonly children: ReactNode;
}): JSX.Element {
  const C = useC();
  return (
    <span
      data-testid={testid}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 1,
        color: C.muted,
      }}
    >
      {children}
    </span>
  );
}

function renderMetaChips(sender: ThreatSenderSummary): JSX.Element | null {
  const seenBefore = typeof sender.prior_alerts_30d === 'number' && sender.prior_alerts_30d > 0
    ? sender.prior_alerts_30d : null;
  const elsewhere = typeof sender.elsewhere_sites_24h === 'number' && sender.elsewhere_sites_24h > 0
    ? sender.elsewhere_sites_24h : null;
  if (seenBefore === null && elsewhere === null) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {seenBefore !== null ? (
        <MetaChip testid={`sender-seen-before-${sender.sender_ref}`}>
          seen before &middot; {seenBefore.toLocaleString('en-US')} in 30 days
        </MetaChip>
      ) : null}
      {elsewhere !== null ? (
        <MetaChip testid={`sender-elsewhere-${sender.sender_ref}`}>
          also on {elsewhere.toLocaleString('en-US')} other Sill {elsewhere === 1 ? 'site' : 'sites'}
        </MetaChip>
      ) : null}
    </div>
  );
}
